import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Folder, FolderOpen, Plus, Search, Settings, Upload } from 'lucide-react';
import { ITEM_TYPES } from '@mkcore/knowledge/schema';
import { Badge, Button, buttonVariants, Card, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton, Tabs, TabsList, TabsTrigger, cn } from '@mkcore/ui';
import { formatDate } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { NewItemDialog, UploadDialog } from '@/features/knowledge/dialogs';
import { documentsQuery, foldersQuery, itemsQuery, spacesQuery, type Space } from '@/features/knowledge/queries';
import { KindIcon, SpaceLabel, TypeBadge } from '@/features/knowledge/ui';

const text = z
  .union([z.string(), z.number()])
  .optional()
  .transform((v) => (v === undefined ? undefined : String(v)));
const search = z.object({
  space: z.string().optional(),
  path: text,
  type: z.enum(ITEM_TYPES).optional(),
  tag: text,
  title: text,
  view: z.enum(['items', 'documents']).optional(),
  new: text,
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/knowledge/')({ validateSearch: search, component: KnowledgePage });

const KIND_ORDER = ['tenant', 'organisation', 'project', 'shared', 'agent'];

/** The knowledge workplace (CO-1107, CO-0499, CO-1095): spaces and their folders, the items and documents in them. */
function KnowledgePage() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const canCreate = useCan('knowledge:create');
  const canManage = useCan('knowledge:manage');
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const spaces = useQuery(spacesQuery());
  const list = useMemo(() => [...(spaces.data?.items ?? [])].sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || a.name.localeCompare(b.name)), [spaces.data]);
  const space = list.find((x) => x.id === s.space) ?? null;
  const view = s.view ?? 'items';
  return (
    <>
      <PageHeader
        title={t('knowledge.title')}
        description={t('knowledge.description')}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/knowledge/search" className={buttonVariants({ variant: 'secondary' })}>
              <Search /> {t('knowledge.search.open')}
            </Link>
            {canCreate && (
              <>
                <Button variant="secondary" onClick={() => setUploading(true)}>
                  <Upload /> {t('knowledge.upload.title')}
                </Button>
                <Button onClick={() => setAdding(true)}>
                  <Plus /> {t('knowledge.item.new')}
                </Button>
              </>
            )}
            {canManage && (
              <Link to="/knowledge/admin" className={buttonVariants({ variant: 'ghost' })}>
                <Settings /> {t('knowledge.admin.title')}
              </Link>
            )}
          </div>
        }
      />
      <div className="grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <aside className="grid content-start gap-4" aria-label={t('knowledge.spaces')}>
          <nav aria-label={t('knowledge.spaces')} className="grid gap-0.5">
            <button type="button" className={cn('rounded px-2 py-1 text-left text-sm hover:bg-surface-alt', !space && 'bg-surface-alt font-bold')} onClick={() => set({ space: undefined, path: undefined })}>
              {t('knowledge.allSpaces')}
            </button>
            {spaces.isLoading ? (
              <Skeleton className="h-24" />
            ) : (
              list.map((x) => (
                <button
                  key={x.id}
                  type="button"
                  aria-current={x.id === space?.id ? 'true' : undefined}
                  className={cn('flex items-center justify-between gap-2 rounded px-2 py-1 text-left text-sm hover:bg-surface-alt', x.id === space?.id && 'bg-surface-alt font-bold')}
                  onClick={() => set({ space: x.id, path: undefined })}
                >
                  <span className="truncate">
                    <SpaceLabel space={x} />
                  </span>
                  <span className="text-xs text-fg-3">{x.itemCount}</span>
                </button>
              ))
            )}
          </nav>
          {space && <Folders space={space} path={s.path ?? ''} onPick={(path) => set({ path: path || undefined })} />}
        </aside>
        <section className="grid content-start gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <Tabs value={view} onValueChange={(v) => set({ view: v === 'items' ? undefined : (v as Search['view']) })}>
              <TabsList>
                <TabsTrigger value="items">{t('knowledge.items')}</TabsTrigger>
                <TabsTrigger value="documents">{t('knowledge.documents')}</TabsTrigger>
              </TabsList>
            </Tabs>
            <Field label={t('knowledge.filter.title')} className="w-64">
              {(p) => <Input {...p} value={s.title ?? ''} onChange={(e) => set({ title: e.target.value || undefined })} />}
            </Field>
            {view === 'items' && (
              <>
                <Field label={t('knowledge.item.fields.type')} className="w-40">
                  {(p) => (
                    <NativeSelect {...p} value={s.type ?? ''} onChange={(e) => set({ type: (e.target.value || undefined) as Search['type'] })}>
                      <option value="">{t('common.all')}</option>
                      {ITEM_TYPES.map((x) => (
                        <option key={x} value={x}>
                          {t(`knowledge.types.${x}`)}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </Field>
                <Field label={t('knowledge.filter.tag')} className="w-40">
                  {(p) => <Input {...p} value={s.tag ?? ''} onChange={(e) => set({ tag: e.target.value || undefined })} />}
                </Field>
              </>
            )}
          </div>
          {s.path && (
            <p className="flex items-center gap-1 text-sm text-fg-2">
              <FolderOpen className="size-4" /> {s.path}
            </p>
          )}
          {view === 'items' ? <Items search={s} /> : <Documents search={s} />}
        </section>
      </div>
      {(adding || s.new) && <NewItemDialog spaceId={space?.id} path={s.path} title={s.new} onClose={() => (setAdding(false), s.new && set({ new: undefined }))} />}
      {uploading && <UploadDialog spaceId={space?.id} path={s.path} onClose={() => setUploading(false)} />}
    </>
  );
}

/** The folders of a space as a tree, with their number of items (CO-0499). */
function Folders({ space, path, onPick }: { space: Space; path: string; onPick: (path: string) => void }) {
  const { t } = useTranslation();
  const folders = useQuery(foldersQuery(space.id));
  const rows = folders.data?.items ?? [];
  return (
    <nav aria-label={t('knowledge.folders')} className="grid gap-0.5">
      <p className="px-2 text-xs font-bold uppercase text-fg-3">{t('knowledge.folders')}</p>
      <button type="button" className={cn('rounded px-2 py-1 text-left text-sm hover:bg-surface-alt', !path && 'bg-surface-alt font-bold')} onClick={() => onPick('')}>
        {t('knowledge.allFolders')}
      </button>
      {rows.map((f) => {
        const depth = f.path.split('/').length - 1;
        return (
          <button
            key={f.path}
            type="button"
            aria-current={f.path === path ? 'true' : undefined}
            className={cn('flex items-center justify-between gap-2 rounded py-1 pr-2 text-left text-sm hover:bg-surface-alt', f.path === path && 'bg-surface-alt font-bold')}
            style={{ paddingLeft: `${0.5 + depth * 0.9}rem` }}
            onClick={() => onPick(f.path)}
          >
            <span className="flex min-w-0 items-center gap-1">
              <Folder className="size-3.5 shrink-0 text-fg-3" />
              <span className="truncate">{f.path.split('/').pop()}</span>
            </span>
            <span className="text-xs text-fg-3">{f.total}</span>
          </button>
        );
      })}
    </nav>
  );
}

function Items({ search: s }: { search: Search }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const q = useInfiniteQuery(itemsQuery({ spaceId: s.space, path: s.path, recursive: s.path ? true : undefined, type: s.type ? [s.type] : undefined, tag: s.tag, title: s.title, limit: 50 }));
  const items = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (q.isLoading) return <Skeleton className="h-64" />;
  if (!items.length) return <EmptyState title={t('knowledge.none')} description={t('knowledge.emptyHint')} />;
  return (
    <>
      <ul className="grid gap-2" aria-label={t('knowledge.items')}>
        {items.map((i) => (
          <li key={i.id}>
            <Card className="grid gap-1 p-3">
              <span className="flex flex-wrap items-center gap-2">
                <KindIcon kind="item" />
                <Link to="/knowledge/items/$id" params={{ id: i.id }} className="font-bold hover:underline">
                  {i.title}
                </Link>
                <TypeBadge type={i.type} />
                {i.tags.map((tag) => (
                  <Badge key={tag} tone="outline">
                    #{tag}
                  </Badge>
                ))}
              </span>
              {i.excerpt && <span className="line-clamp-2 text-sm text-fg-2">{i.excerpt}</span>}
              <span className="flex flex-wrap items-center gap-2 text-xs text-fg-3">
                <SpaceLabel space={i.space} />
                {i.path && <span>{i.path}</span>}
                <span>{formatDate(i.updatedAt.slice(0, 10))}</span>
                {i.createdByKind === 'agent' && <Badge tone="info">{t('knowledge.byAgent')}</Badge>}
              </span>
            </Card>
          </li>
        ))}
      </ul>
      {q.hasNextPage && (
        <div className="flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
    </>
  );
}

function Documents({ search: s }: { search: Search }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const q = useInfiniteQuery(documentsQuery({ spaceId: s.space, path: s.path, recursive: s.path ? true : undefined, title: s.title, limit: 50 }));
  const docs = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (q.isLoading) return <Skeleton className="h-64" />;
  if (!docs.length) return <EmptyState title={t('knowledge.noDocuments')} description={t('knowledge.noDocumentsHint')} />;
  return (
    <>
      <ul className="grid gap-2" aria-label={t('knowledge.documents')}>
        {docs.map((d) => (
          <li key={d.id}>
            <Card className="flex flex-wrap items-center gap-2 p-3">
              <KindIcon kind="document" sourceKind={d.source?.kind} mimeType={d.mimeType} />
              <Link to="/knowledge/documents/$id" params={{ id: d.id }} className="font-bold hover:underline">
                {d.title}
              </Link>
              {d.status === 'failed' && <Badge tone="danger">{t('knowledge.document.failed')}</Badge>}
              {d.hidden && <Badge tone="outline">{t('knowledge.document.hidden')}</Badge>}
              <span className="ml-auto flex items-center gap-2 text-xs text-fg-3">
                {d.source && <span>{d.source.name}</span>}
                <SpaceLabel space={d.space} />
                {formatDate((d.updatedAtSource ?? d.indexedAt).slice(0, 10))}
              </span>
            </Card>
          </li>
        ))}
      </ul>
      {q.hasNextPage && (
        <div className="flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
    </>
  );
}
